import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { ArrowRight, Globe, TrendingUp, Cpu, Star, ChevronRight } from "lucide-react";
import { STATS, BUSINESSES, PRODUCTS, TIMELINE, MODELS } from "@/data/content";

/* ── Shared animation variants ── */
const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};
const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.1 } },
};

function SectionIn({ children, className = "" }: { children: React.ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.div ref={ref} variants={stagger} initial="hidden" animate={inView ? "visible" : "hidden"} className={className}>
      {children}
    </motion.div>
  );
}

function SectionLabel({ text }: { text: string }) {
  return (
    <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/10 text-primary text-xs font-semibold uppercase tracking-widest mb-4">
      <span className="w-1.5 h-1.5 rounded-full bg-primary animate-pulse" />
      {text}
    </motion.div>
  );
}

function SectionHeading({ title, subtitle }: { title: string; subtitle?: string }) {
  return (
    <>
      <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl font-bold text-white mb-4 leading-tight">
        {title}
      </motion.h2>
      {subtitle && (
        <motion.p variants={fadeUp} className="text-white/50 text-base md:text-lg leading-relaxed max-w-2xl">
          {subtitle}
        </motion.p>
      )}
    </>
  );
}

/* ═══════════════════════ HERO ═══════════════════════ */
function Hero() {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden bg-[oklch(0.10_0.05_252)]">
      {/* Star field */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 80 }).map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white"
            style={{
              width: Math.random() * 2 + 0.5 + "px",
              height: Math.random() * 2 + 0.5 + "px",
              top: Math.random() * 100 + "%",
              left: Math.random() * 100 + "%",
              opacity: Math.random() * 0.5 + 0.1,
            }}
          />
        ))}
      </div>

      {/* Gradient orbs */}
      <div className="absolute top-1/4 left-1/3 w-[600px] h-[600px] rounded-full bg-primary/10 blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-cyan-500/8 blur-[100px] pointer-events-none" />
      <div className="absolute top-1/2 left-0 w-[300px] h-[300px] rounded-full bg-accent/5 blur-[80px] pointer-events-none" />

      {/* Grid overlay */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(oklch(1 0 0 / 1) 1px, transparent 1px), linear-gradient(90deg, oklch(1 0 0 / 1) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
        }}
      />

      <div className="relative z-10 max-w-7xl mx-auto px-6 pt-24 pb-16 w-full">
        <div className="max-w-4xl">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-primary/30 bg-primary/10 text-primary text-sm font-medium mb-8"
          >
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            ALL in AGI · 2026 战略全面升级
            <ChevronRight className="w-3.5 h-3.5" />
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.75, ease: [0.22, 1, 0.36, 1] }}
            className="text-5xl md:text-6xl lg:text-7xl font-black text-white leading-[1.1] mb-6 tracking-tight"
          >
            实现通用人工智能
            <br />
            <span className="bg-gradient-to-r from-primary via-cyan-400 to-primary bg-clip-text text-transparent">
              让每个人更好地
            </span>
            <br />
            塑造和表达自我
          </motion.h1>

          {/* Sub */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.65, ease: [0.22, 1, 0.36, 1] }}
            className="text-white/55 text-lg md:text-xl leading-relaxed mb-10 max-w-2xl"
          >
            昆仑万维成立于 2008 年，深交所上市（300418），以 AGI 与 AIGC 为核心战略，
            服务全球 100+ 国家和地区近 <span className="text-white/80 font-semibold">4 亿</span>月活用户。
          </motion.p>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.65, duration: 0.6 }}
            className="flex flex-wrap items-center gap-4"
          >
            <a
              href="https://tiangong.cn"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-3 bg-primary hover:bg-primary/90 text-white rounded-full font-semibold text-sm transition-all duration-200 shadow-lg shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5"
            >
              体验天工 AI <ArrowRight className="w-4 h-4" />
            </a>
            <button
              onClick={() => document.querySelector("#business")?.scrollIntoView({ behavior: "smooth" })}
              className="flex items-center gap-2 px-6 py-3 border border-white/10 hover:border-white/25 text-white/70 hover:text-white rounded-full font-medium text-sm transition-all duration-200 hover:bg-white/5"
            >
              了解业务板块
            </button>
          </motion.div>
        </div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-4"
        >
          {STATS.map((s) => (
            <div
              key={s.label}
              className="p-5 rounded-2xl border border-white/8 bg-white/4 backdrop-blur-sm hover:border-primary/30 hover:bg-primary/5 transition-all duration-300 group"
            >
              <div className="text-3xl md:text-4xl font-black text-accent font-mono mb-1 group-hover:text-accent/90">
                {s.value}
              </div>
              <div className="text-white/75 text-sm font-medium">{s.label}</div>
              <div className="text-white/30 text-xs mt-0.5">{s.sublabel}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll hint */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <div className="w-5 h-8 rounded-full border border-white/20 flex items-start justify-center p-1">
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.4, ease: "easeInOut" }}
            className="w-1 h-2 rounded-full bg-white/40"
          />
        </div>
      </motion.div>
    </section>
  );
}

/* ═══════════════════════ BUSINESS ═══════════════════════ */
function Business() {
  return (
    <section id="business" className="py-24 bg-[oklch(0.11_0.048_252)] relative overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="max-w-7xl mx-auto px-6">
        <SectionIn className="mb-14 text-center">
          <SectionLabel text="三大业务板块" />
          <SectionHeading
            title="驱动全球数字变革的综合科技集团"
            subtitle="昆仑万维以 AGI 技术为核心，联动海外信息分发与科技投资，构建覆盖全球的多元化数字生态体系。"
          />
        </SectionIn>

        <SectionIn className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {BUSINESSES.map((biz, i) => (
            <motion.div
              key={biz.id}
              variants={fadeUp}
              transition={{ delay: i * 0.12 }}
              className={`relative p-7 rounded-3xl border ${biz.borderColor} bg-gradient-to-br ${biz.color} backdrop-blur-sm hover:scale-[1.02] transition-all duration-300 group cursor-default`}
            >
              <div className="text-4xl mb-4">{biz.icon}</div>
              <div className="text-white/40 text-xs uppercase tracking-widest mb-1 font-semibold">{biz.subtitle}</div>
              <h3 className="text-xl font-bold text-white mb-3">{biz.title}</h3>
              <p className="text-white/55 text-sm leading-relaxed mb-5">{biz.description}</p>
              <div className="flex flex-wrap gap-2">
                {biz.highlights.map((h) => (
                  <span
                    key={h}
                    className="px-2.5 py-0.5 rounded-full bg-white/8 border border-white/10 text-white/60 text-xs"
                  >
                    {h}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </SectionIn>
      </div>
    </section>
  );
}

/* ═══════════════════════ PRODUCTS ═══════════════════════ */
const badgeStyles: Record<string, string> = {
  gold: "bg-accent/20 text-accent border-accent/30",
  blue: "bg-primary/20 text-primary border-primary/30",
  green: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  purple: "bg-purple-500/20 text-purple-400 border-purple-500/30",
};

function Products() {
  return (
    <section id="products" className="py-24 bg-[oklch(0.125_0.052_251)] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 right-0 w-[500px] h-[500px] rounded-full bg-primary/6 blur-[100px]" />
        <div className="absolute bottom-0 left-1/4 w-[350px] h-[350px] rounded-full bg-cyan-500/5 blur-[80px]" />
      </div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <SectionIn className="mb-14">
          <SectionLabel text="产品矩阵" />
          <SectionHeading
            title="全链路 AI 产品生态"
            subtitle="覆盖 AI 智能体、视频生成、音乐创作、社交应用与游戏世界模型，构建国内领先的 AIGC 全家桶。"
          />
        </SectionIn>

        <SectionIn className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {PRODUCTS.map((p, i) => (
            <motion.div
              key={p.name}
              variants={fadeUp}
              transition={{ delay: i * 0.08 }}
              className="group relative p-6 rounded-2xl border border-white/8 bg-white/3 hover:border-primary/30 hover:bg-primary/5 transition-all duration-300"
            >
              {/* Icon + badge */}
              <div className="flex items-start justify-between mb-4">
                <div className="text-3xl">{p.icon}</div>
                <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold border ${badgeStyles[p.badgeType]}`}>
                  {p.badge}
                </span>
              </div>
              {/* Category */}
              <div className="text-white/35 text-xs uppercase tracking-widest mb-1 font-medium">{p.category}</div>
              {/* Name */}
              <h3 className="text-white font-bold text-lg mb-0.5">{p.name}</h3>
              <p className="text-white/35 text-xs mb-3">{p.nameEn}</p>
              {/* Desc */}
              <p className="text-white/55 text-sm leading-relaxed">{p.description}</p>
              {/* Hover glow line */}
              <div className="absolute bottom-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-primary/0 to-transparent group-hover:via-primary/30 transition-all duration-500" />
            </motion.div>
          ))}
        </SectionIn>
      </div>
    </section>
  );
}

/* ═══════════════════════ TECHNOLOGY ═══════════════════════ */
function Technology() {
  return (
    <section id="technology" className="py-24 bg-[oklch(0.10_0.045_254)] relative overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/25 to-transparent" />
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left */}
          <SectionIn>
            <SectionLabel text="核心技术" />
            <SectionHeading
              title="多项指标登顶全球 SOTA"
              subtitle="昆仑万维构建了完整的「算力-大模型-应用」技术栈，旗下 Skywork 系列模型在视频、音乐、多模态推理等多个维度实现开源最佳性能。"
            />
            <motion.div variants={fadeUp} className="mt-8 flex flex-col gap-3">
              {[
                { label: "多模态推理", value: 98, color: "bg-primary" },
                { label: "AI 视频生成", value: 92, color: "bg-cyan-500" },
                { label: "AI 音乐创作", value: 96, color: "bg-accent" },
                { label: "代码智能体", value: 90, color: "bg-purple-500" },
              ].map((item) => (
                <div key={item.label} className="flex items-center gap-4">
                  <span className="text-white/50 text-sm w-24 shrink-0">{item.label}</span>
                  <div className="flex-1 h-1.5 bg-white/8 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: item.value + "%" }}
                      transition={{ duration: 1.2, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                      viewport={{ once: true }}
                      className={`h-full rounded-full ${item.color}`}
                    />
                  </div>
                  <span className="text-white/30 text-xs font-mono w-10 text-right">{item.value}%</span>
                </div>
              ))}
            </motion.div>
          </SectionIn>

          {/* Right: Model cards */}
          <SectionIn className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {MODELS.map((m, i) => (
              <motion.div
                key={m.name}
                variants={fadeUp}
                transition={{ delay: i * 0.1 }}
                className="p-4 rounded-xl border border-white/8 bg-white/3 hover:border-primary/25 hover:bg-primary/5 transition-all duration-200 group"
              >
                <div className="flex items-start justify-between gap-2 mb-2">
                  <Cpu className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                  <span className="px-2 py-0.5 rounded-full bg-primary/15 text-primary text-[10px] font-semibold border border-primary/20">
                    {m.tag}
                  </span>
                </div>
                <div className="text-white font-semibold text-sm font-mono leading-tight mb-1">{m.name}</div>
                <div className="text-white/45 text-xs">{m.desc}</div>
              </motion.div>
            ))}
          </SectionIn>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════════════ TIMELINE ═══════════════════════ */
function Timeline() {
  return (
    <section id="timeline" className="py-24 bg-[oklch(0.115_0.05_252)] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-1/4 w-[400px] h-[400px] rounded-full bg-accent/4 blur-[90px]" />
      </div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <SectionIn className="mb-14 text-center">
          <SectionLabel text="发展历程" />
          <SectionHeading
            title="从游戏到 AGI 的蜕变之路"
            subtitle="自 2008 年成立至今，昆仑万维完成了从页游发行商到全球 AGI 科技公司的深度战略转型。"
          />
        </SectionIn>

        <SectionIn className="relative">
          {/* Center line */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-primary/40 via-primary/20 to-transparent -translate-x-1/2" />

          <div className="flex flex-col gap-8">
            {TIMELINE.map((item, i) => {
              const isLeft = i % 2 === 0;
              return (
                <motion.div
                  key={item.year}
                  variants={fadeUp}
                  transition={{ delay: i * 0.06 }}
                  className={`relative flex flex-col md:flex-row items-start md:items-center gap-4 ${
                    isLeft ? "md:flex-row" : "md:flex-row-reverse"
                  }`}
                >
                  {/* Content */}
                  <div className={`flex-1 ${isLeft ? "md:text-right md:pr-8" : "md:pl-8"}`}>
                    <div
                      className={`inline-block p-5 rounded-2xl border border-white/8 bg-white/3 hover:border-primary/25 hover:bg-primary/5 transition-all duration-300 text-left max-w-sm ${
                        isLeft ? "md:ml-auto" : ""
                      }`}
                    >
                      <div className="text-accent text-xs font-mono font-bold mb-1">{item.year}</div>
                      <div className="text-white font-semibold text-sm mb-1">{item.event}</div>
                      <div className="text-white/45 text-xs leading-relaxed">{item.detail}</div>
                    </div>
                  </div>

                  {/* Dot */}
                  <div className="hidden md:flex shrink-0 w-4 h-4 rounded-full bg-primary border-2 border-[oklch(0.115_0.05_252)] shadow-lg shadow-primary/40 z-10" />

                  {/* Spacer */}
                  <div className="flex-1 hidden md:block" />
                </motion.div>
              );
            })}
          </div>
        </SectionIn>
      </div>
    </section>
  );
}

/* ═══════════════════════ GLOBAL ═══════════════════════ */
function GlobalPresence() {
  const regions = [
    { name: "亚太", markets: ["中国大陆", "东南亚", "日韩"], icon: "🌏" },
    { name: "欧洲", markets: ["英国", "德国", "法国", "北欧"], icon: "🌍" },
    { name: "非洲 & 中东", markets: ["尼日利亚", "肯尼亚", "阿联酋", "沙特"], icon: "🌍" },
    { name: "美洲", markets: ["北美", "巴西", "墨西哥"], icon: "🌎" },
  ];

  return (
    <section id="global" className="py-24 bg-[oklch(0.10_0.045_254)] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/3 w-[600px] h-[400px] rounded-full bg-primary/6 blur-[100px]" />
      </div>
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <SectionIn className="mb-14">
          <SectionLabel text="全球布局" />
          <SectionHeading
            title="深耕 100+ 国家与地区"
            subtitle="海外收入占比长期维持 91% 以上，以 Opera 为核心触点，连通非洲、中东、南亚、欧美等新兴增量市场。"
          />
        </SectionIn>

        {/* Key metrics */}
        <SectionIn className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          {[
            { icon: <Globe className="w-5 h-5" />, value: "100+", label: "覆盖国家" },
            { icon: <TrendingUp className="w-5 h-5" />, value: "$4.8亿", label: "Opera 年营收" },
            { icon: <Star className="w-5 h-5" />, value: "4亿", label: "全球月活" },
            { icon: <ArrowRight className="w-5 h-5" />, value: "21.1%", label: "Opera 营收增速" },
          ].map((m) => (
            <motion.div
              key={m.label}
              variants={fadeUp}
              className="p-5 rounded-2xl border border-primary/20 bg-primary/8 text-center hover:border-primary/35 hover:bg-primary/12 transition-all duration-300"
            >
              <div className="flex justify-center text-primary mb-2">{m.icon}</div>
              <div className="text-2xl font-black text-accent font-mono">{m.value}</div>
              <div className="text-white/50 text-xs mt-1">{m.label}</div>
            </motion.div>
          ))}
        </SectionIn>

        {/* Regions */}
        <SectionIn className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {regions.map((r, i) => (
            <motion.div
              key={r.name}
              variants={fadeUp}
              transition={{ delay: i * 0.08 }}
              className="p-5 rounded-2xl border border-white/8 bg-white/3 hover:border-primary/25 hover:bg-primary/5 transition-all duration-300"
            >
              <div className="text-2xl mb-3">{r.icon}</div>
              <div className="text-white font-semibold text-sm mb-2">{r.name}</div>
              <div className="flex flex-col gap-1">
                {r.markets.map((m) => (
                  <span key={m} className="text-white/40 text-xs">{m}</span>
                ))}
              </div>
            </motion.div>
          ))}
        </SectionIn>
      </div>
    </section>
  );
}

/* ═══════════════════════ CONTACT / CTA ═══════════════════════ */
function Contact() {
  return (
    <section id="contact" className="py-24 bg-[oklch(0.115_0.05_252)] relative overflow-hidden">
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[400px] rounded-full bg-primary/8 blur-[80px]" />
      </div>
      <div className="max-w-3xl mx-auto px-6 relative z-10 text-center">
        <SectionIn>
          <SectionLabel text="联系我们" />
          <motion.h2 variants={fadeUp} className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
            共同迈向
            <span className="bg-gradient-to-r from-primary via-cyan-400 to-primary bg-clip-text text-transparent">
              {" "}AGI 时代
            </span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-white/50 text-lg leading-relaxed mb-10">
            无论是商务合作、技术探讨还是媒体咨询，欢迎联系昆仑万维，共同探索 AI 无限可能。
          </motion.p>

          <motion.div variants={fadeUp} className="flex flex-wrap justify-center gap-4">
            <a
              href="http://www.kunlun.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-8 py-3.5 bg-primary hover:bg-primary/90 text-white rounded-full font-semibold transition-all duration-200 shadow-xl shadow-primary/30 hover:shadow-primary/50 hover:-translate-y-0.5"
            >
              访问官方网站 <ArrowRight className="w-4 h-4" />
            </a>
            <a
              href="https://tiangong.cn"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-8 py-3.5 border border-white/15 hover:border-white/30 text-white/75 hover:text-white rounded-full font-medium transition-all duration-200 hover:bg-white/5"
            >
              天工 AI 平台
            </a>
          </motion.div>

          {/* Info cards */}
          <motion.div variants={fadeUp} className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: "股票代码", value: "深交所 · 300418", sub: "Shenzhen Stock Exchange" },
              { label: "成立时间", value: "2008 年", sub: "Founded in 2008" },
              { label: "官方网站", value: "www.kunlun.com", sub: "Official Website" },
            ].map((card) => (
              <div
                key={card.label}
                className="p-5 rounded-2xl border border-white/8 bg-white/3 text-center"
              >
                <div className="text-white/30 text-xs mb-2">{card.label}</div>
                <div className="text-white font-semibold text-sm">{card.value}</div>
                <div className="text-white/25 text-xs mt-1">{card.sub}</div>
              </div>
            ))}
          </motion.div>
        </SectionIn>
      </div>
    </section>
  );
}

/* ═══════════════════════ PAGE EXPORT ═══════════════════════ */
export default function Index() {
  return (
    <main className="dark">
      <Hero />
      <Business />
      <Products />
      <Technology />
      <Timeline />
      <GlobalPresence />
      <Contact />
    </main>
  );
}
